import chroma from "chroma-js";
import type { BaseTheme } from "common/BaseTheme";
import type { CoreTheme } from "sharedUI/styles";
import { coreTheme } from "sharedUI/styles";
import { lookupValue } from "../../theme-util";
import type { OverlayTheme } from "../overlay-theme";

const SHADOW_COLOR = (opacity: number) => `rgba(48, 58, 74, ${opacity})`;

export type WireAppTheme = typeof defaultWireAppTheme & BaseTheme;

// Derived from: https://www.figma.com/file/TM2WVPjdNFoviY07XvNfb5/%E2%9D%96-Library---Pages

const wireAppTheme: CoreTheme = {
    ...coreTheme,

    // - Default
    n0: "#FFFFFF",
    n50: "#F7F8F9",
    n100: "#F3F4F6",
    n200: "#ECEEF2",
    n300: "#E0E2E5",
    n400: "#9CA3AF",
    n500: "#6B7280",
    n600: "#4B5563",
    n700: "#303A4A",
    n800: "#1F2937",
    n900: "#111827",

    // System Primitives
    // - blue
    b100: "#E0E9F3",
    b200: "#D6E2F4",
    b300: "#A0C2F9",
    b400: "#4F78FF", // default
    b500: "#1D4ED8",

    // green
    g100: "#DAEDE2",
    g200: "#CEEBDA",
    g300: "#79DBA2",
    g400: "#10B97A",
    g500: "#059669",

    // red
    r100: "#F2E1E1",
    r200: "#F2D9D8",
    r300: "#F19F9B",
    r400: "#EF4D44",
    r500: "#DC2C26",

    // yellow
    y100: "#F3EDDD",
    y200: "#F3EAD3",
    y300: "#F3EAD3",
    y400: "#FBBF24",
    y500: "#F59E0B",

    textDark: lookupValue("n900"),
    textBase: lookupValue("n700"),
    textPale: lookupValue("n600"),
    textXpale: lookupValue("n500"),
    textDisabled: lookupValue("n400"),
    textAccent: lookupValue("primaryAccentColor"),

    textContextualDark: lookupValue("n900"),
    textContextualBase: lookupValue("n700"),
    textContextualPale: lookupValue("n600"),
    textContextualXpale: lookupValue("n500"),
    textContextualDisabled: lookupValue("n400"),
    textContextualAccent: lookupValue("primaryAccentColor"),

    iconDark: lookupValue("n700"),
    iconBase: lookupValue("n600"),
    iconPale: lookupValue("n500"),
    iconXpale: lookupValue("n400"),
    iconDisabled: lookupValue("n300"),

    bgFront: lookupValue("n0"),
    bgMiddle: lookupValue("n50"),
    bgBack: lookupValue("n100"),
    bgBehind: lookupValue("n200"),

    bgHovered: lookupValue("n100"),

    borderDark: lookupValue("n300"),
    borderBase: lookupValue("n200"),
    borderPale: lookupValue("n100"),
    borderXpale: lookupValue("n50"),

    accent: lookupValue("primaryAccentColor"),
    // Shadows
    // - Soft

    shadowSoft0: `0 0 0 1px ${SHADOW_COLOR(0.08)}`,
    shadowSoft1: `0 1px 2.5px ${SHADOW_COLOR(0.1)}`,
    shadowSoft2: `0 6px 12px -3px ${SHADOW_COLOR(0.1)}`,
    shadowSoft3: `0 16px 20 -6px ${SHADOW_COLOR(0.1)}`,
    shadowSoft4: `0 28px 48px -8px ${SHADOW_COLOR(0.1)}`,
    shadowSoft5: `0 32px 64px -12px ${SHADOW_COLOR(0.12)}`,
    shadowSoft6: `0 48px 96px -10 ${SHADOW_COLOR(0.1)}`,

    // - Base
    shadow0: `0 0 0 1px ${SHADOW_COLOR(0.12)}`,
    shadow1: `0 0 0 0.5px ${SHADOW_COLOR(0.08)}, 0 1px 2.5px ${SHADOW_COLOR(0.1)}`,
    shadow2: `0 0 0 0.5px ${SHADOW_COLOR(0.08)}, 0 6px 12px -3px ${SHADOW_COLOR(0.14)}`,
    shadow3: `0 0 0 0.5px ${SHADOW_COLOR(0.08)}, 0 20px 40px -6px ${SHADOW_COLOR(0.16)}`,
    shadow4: `0 0 0 0.5px ${SHADOW_COLOR(0.08)}, 0 24px 48px -8px ${SHADOW_COLOR(0.18)}`,
    shadow5: `0 0 0 0.5px ${SHADOW_COLOR(0.08)}, 0 48px 64px -8px ${SHADOW_COLOR(0.2)}`,
    shadow6: `0 0 0 0.5px ${SHADOW_COLOR(0.08)}, 0 48px 96px -10px ${SHADOW_COLOR(0.36)}`,

    // - Dark
    shadowDark0: `0 0 0 1px ${SHADOW_COLOR(0.18)}`,
    shadowDark1: `0 0 0 0.5px ${SHADOW_COLOR(0.12)}, 0 1px 3px ${SHADOW_COLOR(0.14)}`,
    shadowDark2: `0 0 0 0.5px ${SHADOW_COLOR(0.12)}, 0 2px 4px ${SHADOW_COLOR(0.06)}, 0 6px 12px -3px ${SHADOW_COLOR(
        0.1
    )}`,
    shadowDark3: `0 0 0 0.5px ${SHADOW_COLOR(0.12)}, 0 3px 6px ${SHADOW_COLOR(0.06)}, 0 20px 40px -6px ${SHADOW_COLOR(
        0.2
    )}`,
    shadowDark4: `0 0 0 0.5px ${SHADOW_COLOR(0.12)}, 0 4px 8px ${SHADOW_COLOR(0.06)}, 0 28px 64px -8px ${SHADOW_COLOR(
        0.32
    )}`,
    shadowDark5: `0 0 0 0.5px ${SHADOW_COLOR(0.12)}, 0 6px 12px ${SHADOW_COLOR(0.06)}, 0 40 64px -10px ${SHADOW_COLOR(
        0.32
    )}`,

    shadowDark6: `0 0 0 0.5px ${SHADOW_COLOR(0.12)}, 0 32px 64px ${SHADOW_COLOR(
        0.08
    )}, 0 48px 96px -10px ${SHADOW_COLOR(0.36)}`,

    // - XDark
    shadowXdark0: `0 0 0 1px ${SHADOW_COLOR(0.22)}`,
    shadowXdark1: `0 0 0 0.5px ${SHADOW_COLOR(0.16)}, 0 1px 3px ${SHADOW_COLOR(0.24)}`,
    shadowXdark2: `0 0 0 0.5px ${SHADOW_COLOR(0.16)}, 0 2px 4px ${SHADOW_COLOR(0.06)}, 0 4px 12px -2px ${SHADOW_COLOR(
        0.14
    )}`,
    shadowXdark3: `0 0 0 1px ${SHADOW_COLOR(0.14)}, 0 3px 6px ${SHADOW_COLOR(0.06)}, 0 20px 40px -6px ${SHADOW_COLOR(
        0.2
    )}`,
    shadowXdark4: `0 0 0 0.5px ${SHADOW_COLOR(0.16)}, 0 6px 12px ${SHADOW_COLOR(0.06)}, 0 28px 64px -8px ${SHADOW_COLOR(
        0.32
    )}`,
    shadowXdark5: `0 0 0 0.5px ${SHADOW_COLOR(0.16)}, 0 16px 24px -4px ${SHADOW_COLOR(
        0.16
    )}, 0 40 64px -4px ${SHADOW_COLOR(0.2)}`,
    shadowXdark6: `0 0 0 0.5px ${SHADOW_COLOR(0.16)}, 0 20 32px -4px ${SHADOW_COLOR(
        0.14
    )}, 0 48px 96px -6px ${SHADOW_COLOR(0.42)}`,
};

export const defaultWireAppAccentContextOverlay: Partial<WireAppTheme> & OverlayTheme = {
    overlayName: "lightContext",
    textContextualAccent: "rgba(255, 255, 255, 1)",
    textContextualBase: "rgba(255, 255, 255, 0.9)",
    textContextualDark: "rgba(255, 255, 255, 1)",
    textContextualDisabled: "rgba(255, 255, 255, 0.6)",
    textContextualInverse: lookupValue("n900"),
    textContextualPale: "rgba(255, 255, 255, 0.8)",
    textContextualXpale: "rgba(255, 255, 255, 0.7)",
    borderBase: "rgba(255, 255, 255, 0.15)",
    bgHovered: "rgba(255, 255, 255, 0.1)",
};

export const highlightBackgroundWireAppOverlay: Partial<WireAppTheme> & OverlayTheme = {
    overlayName: "highlightBackground",
    bgContainerBase: lookupValue("n0"),
    bgContainerHighlight: lookupValue("n50"),
};

export const defaultWireAppImageBrightenContextOverlay: Partial<WireAppTheme> & OverlayTheme = {
    overlayName: "imageBrightenContext",
    textContextualAccent: lookupValue("textAccent", s => chroma(s).darken(0.5).css()),
};

export const defaultWireAppImageDarkenContextOverlay: Partial<WireAppTheme> & OverlayTheme = {
    overlayName: "imageDarkenContext",
    textContextualAccent: lookupValue("textAccent"),
    textContextualBase: "rgba(255, 255, 255, 0.9)",
    textContextualDark: "rgba(255, 255, 255, 1)",
    textContextualDisabled: "rgba(255, 255, 255, 0.6)",
    textContextualInverse: lookupValue("n900"),
    textContextualPale: "rgba(255, 255, 255, 0.8)",
    textContextualXpale: "rgba(255, 255, 255, 0.7)",
};

export const defaultWireAppAccentContextOverlayUndo: Partial<WireAppTheme> & OverlayTheme = {
    overlayName: "lightContextUndo",
    textContextualAccent: lookupValue("textAccent"),
    textContextualBase: lookupValue("textBase"),
    textContextualDark: lookupValue("textDark"),
    textContextualDisabled: lookupValue("textDisabled"),
    textContextualInverse: lookupValue("textInverse"),
    textContextualPale: lookupValue("textPale"),
    textContextualXpale: lookupValue("textXpale"),
    borderBase: lookupValue("n200"),
    bgHovered: lookupValue("n100"),
};

export const defaultWireAppTheme = {
    overlayName: "defaultWireApp",

    ...wireAppTheme,
    // start of note: These aren't in the appCoreTheme because we want to stop using them in favor of textDark, textBase
    fgColorOffBase: lookupValue("n600"),
    fgColorLight: lookupValue("n600"),
    fgColorMedium: lookupValue("n700"),
    fgColorDark: lookupValue("n800"),
    fgColorOpaque: lookupValue("n900"),
    baseColor: lookupValue("n0"),
    offBaseColor: lookupValue("n100"),
    skeletonColor: lookupValue("n200"),
    linkColor: "#106ba3",
    separatorColor: "rgba(0, 0, 0, 0.12)",
    veilColor: "rgba(0, 0, 0, 0.2)",
    destroyColor: lookupValue("r500"),

    primaryOverlayColor: "#FFFFFFF9",
    transparentOverlayColor: "#FFFFFFC9",
    accentForegroundColor: lookupValue("n0"),
    // end of note

    builderWidth: 375,
    builderHeight: 812,

    navBar: {
        height: 44,
        heightWithSearch: 96,
        titleFontStyle: lookupValue<any>("fontStyles.AccentCallout4Fixed"),
        style: "iOS",
        fgColor: "_primaryAccentColor",
        titleColor: "_fgColorDark",
        bgColor: "_primaryOverlayColor",
        transparentBgColor: "_transparentOverlayColor",
        fadeTo: "white",
        cancelColor: "_fgColorDark",

        largeTitleHeight: 0,
        borderRadius: 0,
    },
    preferredRoundingRadius: 10,

    tileItem: {
        padding: 0,
        borderRadius: 6,
        selectedColor: "none",
    },

    button: {
        filledTextColor: "white",
        borderRadius: 10,
        height: 44,
        uppercase: false,
        shadow: "unset",
        fontStyle: lookupValue<any>("fontStyles.Subheader4"),
    },

    formSubmissionSuccessToast: {
        background: "rgba(230, 230, 230, 0.96)",
        translucentBackground: "rgba(230, 230, 230, 0.6)",
        checkmark: "black",
        labelColor: "#000000",
    },
    youtube: {
        defaultHeight: 300,
    },

    picker: {
        backgroundColor: "rgba(0, 0, 0, 0.05)",
        hoverBgColor: "rgba(0, 0, 0, 0.075)",
        activeBgColor: "rgba(0, 0, 0, 0.12)",
        currentBgColor: "_baseColor",
        currentColor: "_primaryAccentColor",
    },

    wireappTest: {
        silly: "#00FFFF",
    },
    textField: {
        underlineColor: "rgba(0, 0, 0, 0.15)",
    },
    statusBar: {
        color: "#000",
        height: 44,
    },

    homeBar: {
        color: "#000",
        height: 34,
    },

    materialDateTimePicker: {
        borderRadius: "12",
        borderWidth: "0",
        borderColor: "transparent",
        borderStyle: "none",

        header: {
            background: "_baseColor",
            textDark: "_fgColorDark",
            textLight: "_fgColorLight",
        },
        tabs: {
            background: "_baseColor",
            iconColor: "_fgColorMedium",
            iconSelectedColor: "_primaryAccentColor",
        },
        cancelConfirm: {
            bg: "_primaryAccentColor",
            fg: "_accentForegroundColor",
        },
    },
};
